import { Tooltip, Typography, Box, TooltipProps, TypographyProps, BoxProps } from "@mui/material";
import { useMemo, memo } from "react";
import DOMPurify from "dompurify";

const stripHtml = (html: string | null | undefined) => html?.replace(/<[^>]*>/g, "").trim() || "";

interface TooltipDataGridCellProps {
  notes: string | null | undefined;
  isRichText?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  typographyProps?: Partial<TypographyProps>;
  tooltipContentBoxProps?: Partial<BoxProps>;
}

export const TooltipDataGridCell: React.FC<TooltipDataGridCellProps> = memo(
  ({ notes, isRichText = false, tooltipProps = {}, typographyProps = {}, tooltipContentBoxProps = {} }) => {
    const { plainText, safeHtml } = useMemo(() => {
      return {
        plainText: stripHtml(notes),
        safeHtml: DOMPurify.sanitize(notes || "")
      };
    }, [notes]);

    if (!notes || !plainText) {
      return (
        <Typography variant="body2" {...typographyProps}>
          --
        </Typography>
      );
    }

    const tooltipContent = (
      <Box
        sx={{
          ...tooltipContentBoxProps.sx
        }}
        {...tooltipContentBoxProps}>
        <Typography
          variant="body2"
          component="span"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
          sx={{
            '& [style*="background-color"]': {
              color: "text.primary"
            }
          }}
        />
      </Box>
    );

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center"
        }}>
        <Tooltip
          title={tooltipContent}
          disableInteractive={false}
          {...(isRichText && {
            componentsProps: {
              ...tooltipProps.componentsProps,
              tooltip: {
                ...tooltipProps.componentsProps?.tooltip
              }
            }
          })}
          {...tooltipProps}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              display: "inline-block",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              color: "inherit",
              ...typographyProps.sx
            }}
            {...typographyProps}>
            {plainText}
          </Typography>
        </Tooltip>
      </Box>
    );
  }
);
